import type { InventoryInformationView } from "@slfinrtl/ui-bff-routes"
import { getInventoryInformationViewRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { Stage } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff } from "src/hooks"
import { useVehicleDetailsContext } from "../../context/VehicleDetailsContext"
import { ConditionAndServiceContainer } from "./conditionAndServiceContainer/ConditionAndServiceContainer"
import { ConditionAndServiceSkeleton } from "./conditionAndServiceContainer/components/ConditionAndServiceSkeleton"
import { conditionAndServiceContainerMessages } from "./conditionAndServiceContainer/conditionAndServiceContainer.messages"
import { InventoryAndIntakeContainer } from "./inventoryAndIntakeContainer/InventoryAndIntakeContainer"
import { InventoryAndIntakeSkeleton } from "./inventoryAndIntakeContainer/components/InventoryAndIntakeSkeleton"
import { inventoryAndIntakeContainerMessages } from "./inventoryAndIntakeContainer/inventoryAndIntakeContainer.messages"
import { PricingContainer } from "./pricingContainer/PricingContainer"
import { PricingSkeleton } from "./pricingContainer/components/PricingSkeleton"
import { pricingContainerMessages } from "./pricingContainer/pricingContainer.messages"

export const InventoryInformationContainer = withErrorHandler(() => {
	const { inventoryId, notify } = useVehicleDetailsContext()
	const [isLoading, setIsLoading] = useState(false)
	const [inventoryInformationView, setInventoryInformationView] = useState<InventoryInformationView | null>()
	const { formatMessage } = useIntl()

	const fetchView = useBff(
		getInventoryInformationViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	useEffect(() => {
		function loadInventoryInformation() {
			if (inventoryInformationView !== undefined || isLoading) {
				return
			}
			setIsLoading(true)
			fetchView()
				.then(setInventoryInformationView)
				.catch(() => setInventoryInformationView(null))
				.finally(() => setIsLoading(false))
		}

		void loadInventoryInformation()
	}, [fetchView, inventoryInformationView, isLoading])

	useEffect(() => {
		if (inventoryInformationView !== undefined) {
			notify({
				event: "inventoryInformationViewChanged",
				payload: inventoryInformationView,
			})
		}
	}, [inventoryInformationView, notify])

	return (
		<>
			{inventoryInformationView?.inventoryAndIntake ? (
				<InventoryAndIntakeContainer
					view={inventoryInformationView?.inventoryAndIntake}
					setView={setInventoryInformationView}
				/>
			) : (
				<Stage title={formatMessage(inventoryAndIntakeContainerMessages.title)}>
					{inventoryInformationView === undefined ? (
						<InventoryAndIntakeSkeleton />
					) : (
						<ErrorNotificationInline dismissButton={false} />
					)}
				</Stage>
			)}
			{inventoryInformationView?.conditionAndService ? (
				<ConditionAndServiceContainer
					view={inventoryInformationView.conditionAndService}
					setView={setInventoryInformationView}
				/>
			) : (
				<Stage title={formatMessage(conditionAndServiceContainerMessages.title)}>
					{inventoryInformationView === undefined ? (
						<ConditionAndServiceSkeleton />
					) : (
						<ErrorNotificationInline dismissButton={false} />
					)}
				</Stage>
			)}
			{inventoryInformationView?.pricing ? (
				<PricingContainer view={inventoryInformationView.pricing} setView={setInventoryInformationView} />
			) : (
				<Stage title={formatMessage(pricingContainerMessages.headline)}>
					{inventoryInformationView === undefined ? (
						<PricingSkeleton />
					) : (
						<ErrorNotificationInline dismissButton={false} />
					)}
				</Stage>
			)}
		</>
	)
})
